var LineItemQuantity = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this._onQuantityChanged);
    this.addEventListener("click", this._onRemoveLinkClicked);
  }
  connectedCallback() {
    this.querySelectorAll("button.cus_quantity__button").forEach(button => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        const input = this.querySelector(".quantity-input");
        if (!input) return;
        const lineKey = input.getAttribute("data-line-key");
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max);
        let quantity = parseInt(input.value);
        if (button.name === "minus" && quantity > 0) { quantity -= 1; }
        else if (button.name === "plus" && (isNaN(max) || quantity < max)) { quantity += 1; }
        else { return; }
        input.value = quantity;
        if (lineKey) { this._changeLineItemQuantity(lineKey, quantity); }
      });
    });
  }
  _onQuantityChanged(event) {
    if (!event.target.hasAttribute("data-line-key")) {
      return;
    }
    this._changeLineItemQuantity(event.target.getAttribute("data-line-key"), parseInt(event.target.value));
  }
  _onRemoveLinkClicked(event) {
    if (event.target.tagName !== "A" || !event.target.href.includes("/cart/change")) {
      return;
    }
    event.preventDefault();
    const url = new URL(event.target.href);
    this._changeLineItemQuantity(url.searchParams.get("id"), parseInt(url.searchParams.get("quantity")));
  }
  async _changeLineItemQuantity(lineKey, targetQuantity) {
    if (window.themeVariables.settings.pageType === "cart") {
      window.location.href = `${Shopify.routes.root}cart/change?id=${lineKey}&quantity=${targetQuantity}`;
    } else {
      const lineItem = this.closest("line-item");
      lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity } }));
      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

      // ── Fault-tolerant cart fetch (fixes RC-5 + RC-6) ──────────────────────────
      // Previously: no try/catch, no response.ok check, no content-type guard.
      // If Shopify returned 429 (rate limit) or 5xx, response.json() threw an
      // unhandled exception. The catch bubbled up through _changeLineItemQuantity,
      // no events were dispatched, and the cart UI froze. Additionally,
      // cartContent["items"].filter() threw TypeError when cartContent was null.
      //
      // Fix: validate every layer — network error, HTTP status, content-type, and
      // the shape of the response body — before touching cart data. On any failure,
      // dispatch a cart:refresh so the UI can recover from the server's current state.
      let cartContent = null;
      try {
        const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: lineKey,
            quantity: targetQuantity,
            sections: sectionsToBundle
          })
        });

        if (!response.ok) {
          console.warn(`LineItemQuantity: cart/change.js returned ${response.status} for line ${lineKey}`);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        const ct = response.headers.get("content-type") || "";
        if (!ct.includes("application/json")) {
          // Shopify returns HTML for 429 rate-limit pages and CDN error pages.
          console.warn("LineItemQuantity: unexpected content-type:", ct);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        try {
          cartContent = await response.json();
        } catch (parseError) {
          console.warn("LineItemQuantity: JSON parse error", parseError);
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        // Guard against a malformed response body (e.g. {}) — .filter on undefined
        // was the source of the "filter of undefined" TypeError in the original code.
        if (!cartContent || !Array.isArray(cartContent.items)) {
          console.warn("LineItemQuantity: cart response missing items array");
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

      } catch (networkError) {
        // Covers DNS failure, CORS, fetch abort, etc.
        console.error("LineItemQuantity: network error on cart/change.js", networkError);
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        return;
      }

      const lineItemAfterChange = cartContent.items.filter((item) => item.key === lineKey);
      lineItem?.dispatchEvent(new CustomEvent("line-item:change", {
        bubbles: true,
        detail: {
          quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0].quantity,
          cart: cartContent
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "line-item:change",
          cart: cartContent
        }
      }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  }
};
if (!window.customElements.get("line-item-quantity")) {
  window.customElements.define("line-item-quantity", LineItemQuantity);
}

// --- Cart Variant Selector logic ---

// Helper function to safely disable/enable interactions during variant AJAX
function toggleLineItemControls(lineItemNode, disable) {
  if (!lineItemNode) return;
  const interactives = lineItemNode.querySelectorAll("button, input, select, a");
  interactives.forEach(el => {
    if (el.tagName === "A") {
      if (disable) {
        el.dataset.oldPointerEvents = el.style.pointerEvents || "";
        el.dataset.oldOpacity = el.style.opacity || "";
        el.style.pointerEvents = "none";
        el.style.opacity = "0.5";
      } else {
        el.style.pointerEvents = el.dataset.oldPointerEvents || "";
        el.style.opacity = el.dataset.oldOpacity || "";
      }
    } else {
      // The variant selector itself is disabled independently as a guard; everything else we toggle natively
      if (!el.classList.contains("cart-variant-selector")) {
        el.disabled = disable;
      }
    }
  });
}

document.addEventListener("change", async (event) => {
  if (event.target.classList.contains("cart-variant-selector")) {
    const select = event.target;
    if (select.disabled) return;
    
    // Grab the parent line-item to hook into existing loader system
    const lineItemNode = select.closest("line-item");
    
    const oldLineKey = select.getAttribute("data-line-key");
    const newVariantId = select.value;
    
    // Find quantity either from existing input or fallback to data attribute
    const qtyInput = lineItemNode?.querySelector(".quantity-input");
    const targetQuantity = qtyInput ? parseInt(qtyInput.value, 10) : parseInt(select.getAttribute("data-quantity"), 10);
    
    select.disabled = true;
    toggleLineItemControls(lineItemNode, true);

    try {
      // 1. Activate loader natively via "line-item:will-change"
      lineItemNode?.dispatchEvent(new CustomEvent("line-item:will-change", { 
        bubbles: true, 
        detail: { targetQuantity } 
      }));

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

      // 2. Remove old line item by setting quantity to 0
      const removeRes = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: oldLineKey,
          quantity: 0
        })
      });

      if (!removeRes.ok) throw new Error("Failed to remove old variant");

      // 3. Add new variant
      const addRes = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [{ id: newVariantId, quantity: targetQuantity }]
        })
      });
      
      if (!addRes.ok) throw new Error("Failed to add new variant");

      // 4. Obtain full standard Shopify Cart obj with requested sections
      const cartRes = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updates: {},
          sections: sectionsToBundle
        })
      });
      
      if (!cartRes.ok) throw new Error("Failed to fetch full updated cart state");
      
      const cartContent = await cartRes.json();
      
      toggleLineItemControls(lineItemNode, false);
      
      // Check if we are currently explicitly viewing the isolated Cart page (supports localized paths like /en/cart)
      const isCartPage = /\/cart\/?$/.test(window.location.pathname) || (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === "cart");
      
      if (isCartPage) {
        window.location.reload();
      } else {
        // 5. Deactivate loader explicitly simulating native "quantity: 0" behavior on old line
        const lineItemAfterChange = cartContent.items.filter((item) => item.key === oldLineKey);
        lineItemNode?.dispatchEvent(new CustomEvent("line-item:change", {
          bubbles: true,
          detail: {
            quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0].quantity,
            cart: cartContent
          }
        }));

        // Dispatch necessary events to refresh the cart UI natively
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "line-item:change",
            cart: cartContent
          }
        }));
        
        document.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent
          }
        }));
      }
      
    } catch (error) {
      console.error("Cart Variant Swap Error:", error);
      select.disabled = false;
      toggleLineItemControls(lineItemNode, false);
      
      // Deactivate loader safely on error
      lineItemNode?.dispatchEvent(new CustomEvent("line-item:change", {
        bubbles: true,
        detail: { quantity: targetQuantity }
      }));
      
      document.dispatchEvent(new CustomEvent("cart:refresh")); // Reset state to fallback
    }
  }
});